import { forbidExtraProps } from 'airbnb-prop-types';
import PropTypes from 'prop-types';
import React from 'react';

import { DayPickerKeyboardShortcutsPhrases } from '../defaultPhrases';
import getPhrasePropTypes from '../utils/getPhrasePropTypes';

import mergeClassNames from '../utils/mergeClassNames';
import CloseButton from './CloseButton';
import KeyboardShortcutRow from './KeyboardShortcutRow';

export const TOP_LEFT = 'top-left';
export const TOP_RIGHT = 'top-right';
export const BOTTOM_RIGHT = 'bottom-right';

const propTypes = forbidExtraProps({
  block: PropTypes.bool,
  // TODO: rename button location to be direction-agnostic
  buttonLocation: PropTypes.oneOf([TOP_LEFT, TOP_RIGHT, BOTTOM_RIGHT]),
  showKeyboardShortcutsPanel: PropTypes.bool,
  openKeyboardShortcutsPanel: PropTypes.func,
  closeKeyboardShortcutsPanel: PropTypes.func,
  phrases: PropTypes.shape(getPhrasePropTypes(DayPickerKeyboardShortcutsPhrases)),
  renderKeyboardShortcutsButton: PropTypes.func,
  renderKeyboardShortcutsPanel: PropTypes.func,
});

const defaultProps = {
  block: false,
  buttonLocation: BOTTOM_RIGHT,
  showKeyboardShortcutsPanel: false,
  openKeyboardShortcutsPanel() {},
  closeKeyboardShortcutsPanel() {},
  phrases: DayPickerKeyboardShortcutsPhrases,
  renderKeyboardShortcutsButton: undefined,
  renderKeyboardShortcutsPanel: undefined,
};

function getKeyboardShortcuts(phrases) {
  return [
    {
      unicode: '↵',
      label: phrases.enterKey,
      action: phrases.selectFocusedDate,
    },
    {
      unicode: '←/→',
      label: phrases.leftArrowRightArrow,
      action: phrases.moveFocusByOneDay,
    },
    {
      unicode: '↑/↓',
      label: phrases.upArrowDownArrow,
      action: phrases.moveFocusByOneWeek,
    },
    {
      unicode: 'PgUp/PgDn',
      label: phrases.pageUpPageDown,
      action: phrases.moveFocusByOneMonth,
    },
    {
      unicode: 'Home/End',
      label: phrases.homeEnd,
      action: phrases.moveFocustoStartAndEndOfWeek,
    },
    {
      unicode: 'Esc',
      label: phrases.escape,
      action: phrases.returnFocusToInput,
    },
    {
      unicode: '?',
      label: phrases.questionMark,
      action: phrases.openThisPanel,
    },
  ];
}

class DayPickerKeyboardShortcuts extends React.PureComponent {
  constructor(...args) {
    super(...args);

    const { phrases } = this.props;
    this.keyboardShortcuts = getKeyboardShortcuts(phrases);

    this.onShowKeyboardShortcutsButtonClick = this.onShowKeyboardShortcutsButtonClick.bind(this);
    this.setShowKeyboardShortcutsButtonRef = this.setShowKeyboardShortcutsButtonRef.bind(this);
    this.setHideKeyboardShortcutsButtonRef = this.setHideKeyboardShortcutsButtonRef.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
  }

  componentWillReceiveProps(nextProps) {
    const { phrases } = this.props;
    if (nextProps.phrases !== phrases) {
      this.keyboardShortcuts = getKeyboardShortcuts(nextProps.phrases);
    }
  }

  componentDidUpdate() {
    this.handleFocus();
  }

  handleFocus() {
    if (this.hideKeyboardShortcutsButton) {
      // automatically move focus into the dialog by moving
      // to the only interactive element, the hide button
      this.hideKeyboardShortcutsButton.focus();
    }
  }

  onKeyDown(e) {
    e.stopPropagation();

    const { closeKeyboardShortcutsPanel } = this.props;
    // Because the close button is the only focusable element inside of the panel, this
    // amounts to a very basic focus trap. The user can exit the panel by "pressing" the
    // close button or hitting escape
    switch (e.key) {
      case 'Escape':
        closeKeyboardShortcutsPanel();
        break;

      // do nothing - this allows the up and down arrows continue their
      // default behavior of scrolling the content of the Keyboard Shortcuts Panel
      // which is needed when only a single month is shown for instance.
      case 'ArrowUp':
      case 'ArrowDown':
        break;

      // completely block the rest of the keys that have functionality outside of this panel
      case 'Tab':
      case 'Home':
      case 'End':
      case 'PageUp':
      case 'PageDown':
      case 'ArrowLeft':
      case 'ArrowRight':
        e.preventDefault();
        break;

      default:
        break;
    }
  }

  onShowKeyboardShortcutsButtonClick() {
    const { openKeyboardShortcutsPanel } = this.props;

    // we want to return focus to this button after closing the keyboard shortcuts panel
    openKeyboardShortcutsPanel(() => { this.showKeyboardShortcutsButton.focus(); });
  }

  setShowKeyboardShortcutsButtonRef(ref) {
    this.showKeyboardShortcutsButton = ref;
  }

  setHideKeyboardShortcutsButtonRef(ref) {
    this.hideKeyboardShortcutsButton = ref;
  }

  render() {
    const {
      block,
      buttonLocation,
      showKeyboardShortcutsPanel,
      closeKeyboardShortcutsPanel,
      phrases,
      renderKeyboardShortcutsButton,
      renderKeyboardShortcutsPanel,
    } = this.props;

    const toggleButtonText = showKeyboardShortcutsPanel
      ? phrases.hideKeyboardShortcutsPanel
      : phrases.showKeyboardShortcutsPanel;

    const bottomRight = buttonLocation === BOTTOM_RIGHT;
    const topRight = buttonLocation === TOP_RIGHT;
    const topLeft = buttonLocation === TOP_LEFT;

    return (
      <div>
        {renderKeyboardShortcutsButton
          && renderKeyboardShortcutsButton({
            // passing in context-specific props
            ref: this.setShowKeyboardShortcutsButtonRef,
            onClick: this.onShowKeyboardShortcutsButtonClick,
            ariaLabel: toggleButtonText,
          })}
        {!renderKeyboardShortcutsButton && (
          <button
            ref={this.setShowKeyboardShortcutsButtonRef}
            className={mergeClassNames(
              'DayPickerKeyboardShortcuts_buttonReset',
              'DayPickerKeyboardShortcuts_show',
              bottomRight && 'DayPickerKeyboardShortcuts_show__bottomRight',
              topRight && 'DayPickerKeyboardShortcuts_show__topRight',
              topLeft && 'DayPickerKeyboardShortcuts_show__topLeft',
            )}
            type="button"
            aria-label={toggleButtonText}
            onClick={this.onShowKeyboardShortcutsButtonClick}
            onMouseUp={(e) => {
              e.currentTarget.blur();
            }}
          >
            <span
              className={mergeClassNames(
                'DayPickerKeyboardShortcuts_showSpan',
                bottomRight && 'DayPickerKeyboardShortcuts_showSpan__bottomRight',
                topRight && 'DayPickerKeyboardShortcuts_showSpan__topRight',
                topLeft && 'DayPickerKeyboardShortcuts_showSpan__topLeft',
              )}
            >
              ?
            </span>
          </button>
        )}
        {showKeyboardShortcutsPanel && (
          renderKeyboardShortcutsPanel ? (
            renderKeyboardShortcutsPanel({
              closeButtonAriaLabel: phrases.hideKeyboardShortcutsPanel,
              keyboardShortcuts: this.keyboardShortcuts,
              onCloseButtonClick: closeKeyboardShortcutsPanel,
              onKeyDown: this.onKeyDown,
              title: phrases.keyboardShortcuts,
            })
          ) : (
            <div
              className={mergeClassNames('DayPickerKeyboardShortcuts_panel')}
              role="dialog"
              aria-labelledby="DayPickerKeyboardShortcuts_title"
              aria-describedby="DayPickerKeyboardShortcuts_description"
            >
              <div
                className={mergeClassNames('DayPickerKeyboardShortcuts_title')}
                id="DayPickerKeyboardShortcuts_title"
              >
                {phrases.keyboardShortcuts}
              </div>

              <button
                ref={this.setHideKeyboardShortcutsButtonRef}
                className={mergeClassNames(
                  'DayPickerKeyboardShortcuts_buttonReset',
                  'DayPickerKeyboardShortcuts_close',
                )}
                type="button"
                tabIndex="0"
                aria-label={phrases.hideKeyboardShortcutsPanel}
                onClick={closeKeyboardShortcutsPanel}
                onKeyDown={this.onKeyDown}
              >
                <CloseButton className={mergeClassNames('DayPickerKeyboardShortcuts_closeSvg')} />
              </button>

              <ul
                className={mergeClassNames('DayPickerKeyboardShortcuts_list')}
                id="DayPickerKeyboardShortcuts_description"
              >
                {this.keyboardShortcuts.map(({ unicode, label, action }) => (
                  <KeyboardShortcutRow
                    key={label}
                    unicode={unicode}
                    label={label}
                    action={action}
                    block={block}
                  />
                ))}
              </ul>
            </div>
          )
        )}
      </div>
    );
  }
}

DayPickerKeyboardShortcuts.propTypes = propTypes;
DayPickerKeyboardShortcuts.defaultProps = defaultProps;

export { DayPickerKeyboardShortcuts as PureDayPickerKeyboardShortcuts };
export default DayPickerKeyboardShortcuts;
